import React, { useCallback, useEffect, useMemo, useState } from 'react'
import PageHeader from 'components/atoms/PageHeader'
import styled, { css } from 'styled-components'
import ExpandablePanel from 'components/atoms/ExpandablePanel'
import Modal from 'components/atoms/Modal'
import Input from 'components/atoms/Input'
import { getCurrentUser, selectAccreditationTypes, selectPublicApiToken, selectTwoFactorStatus } from 'features/auth/ducks/selectors'
import { useDispatch, useSelector } from 'react-redux'
import InputDescription from 'features/createBp/components/atoms/InputDescription'
import NextButton from 'features/createBp/components/atoms/NextButton'
import {
  changePasswordRoutine, clearPublicApiTokenRoutine, deleteAccountRoutine,
  getAccreditationTypesRoutine,
  getTwoFactorStatusRoutine,
  getPublicApiTokenRoutine,
  disableTwoFactorRoutine,
  regenerateTwoFactorRecoveryCodesRoutine,
  updateUserRoutine
} from 'features/auth/ducks/actions'
import { head, isEmpty, pathOr, propOr, remove, update, values } from 'ramda'
import ProfileInfo from 'features/profileSettings/components/ProfileInfo'
import { AccreditationForm } from 'features/auth/SignUp'
import { isNotNilOrEmpty } from 'utils/ramda'
import UserBox from 'features/profileSettings/components/UserBox'
import CertificationsPanel from 'features/profileSettings/components/CertificationsPanel'
import GuruSettingsPanel from 'features/profileSettings/components/GuruSettingsPanel'
import WhatsAppPanel from 'features/profileSettings/components/WhatsAppPanel'
import WhatsAppPrivacyExplainer from 'features/profileSettings/components/WhatsAppPrivacyExplainer'
import LocationSharingPanel from 'features/profileSettings/components/LocationSharingPanel'
import OrganisationPanel from 'features/profileSettings/components/OrganisationPanel'
import WhatsAppIcon from '@mui/icons-material/WhatsApp'
import AccountCircleIcon from '@mui/icons-material/AccountCircle'
import CloseIcon from '@mui/icons-material/Close'
import { PRIVATE_PATHS, redirect } from 'utils/paths'

const emptyPasswordValues = {
  currentPassword: '',
  newPassword: '',
  newPasswordConfirmation: ''
}

const ProfileSettings = () => {
  const dispatch = useDispatch()
  const currentUser = useSelector(getCurrentUser)
  const [passwordValues, setPasswordValues] = useState(emptyPasswordValues)
  const [accreditationsValid, setAccreditationsValid] = useState(false)
  const [accreditationsValues, setAccreditationsValues] = useState(
    isNotNilOrEmpty(currentUser.accreditations)
      ? currentUser.accreditations.map(acc => ({
        id: pathOr('', ['pivot', 'accreditationId'], acc),
        number: pathOr('', ['pivot', 'accreditationNumber'], acc),
      }))
      : [{
        id: '',
        number: ''
      }]
  )
  const [userPassword, setUserPassword] = useState('')
  const [twoFactorPassword, setTwoFactorPassword] = useState('')
  const [twoFactorCode, setTwoFactorCode] = useState('')
  const [newRecoveryCodes, setNewRecoveryCodes] = useState([])
  const [disableTwoFactorModalOpen, setDisableTwoFactorModalOpen] = useState(false)
  const [mobileDrawerOpen, setMobileDrawerOpen] = useState(false)
  const isFrs = currentUser.type === 'emergency_service'
  const accreditationTypes = useSelector(selectAccreditationTypes)
  const apiToken = useSelector(selectPublicApiToken)
  const twoFactorStatus = useSelector(selectTwoFactorStatus)
  const isTwoFactorEnabled = twoFactorStatus?.enabled ?? currentUser.twoFactorEnabled

  useEffect(() => {
    dispatch(getAccreditationTypesRoutine())
    dispatch(getTwoFactorStatusRoutine())

    return () => {
      dispatch(clearPublicApiTokenRoutine())
    }
  }, [])

  const accreditationOptions = useMemo(() => {
    return accreditationTypes
      ? [
        {
          label: '',
          value: ''
        },
        ...accreditationTypes.map(type => ({
          label: type.name,
          value: type.id
        }))
      ]
      : []
  }, [accreditationTypes])

  const handlePasswordValueChange = (name, value) =>
    setPasswordValues({ ...passwordValues, [name]: value })

  const clearValues = () => setPasswordValues(emptyPasswordValues)

  const handleSubmit = e => {
    e.preventDefault()
    dispatch(changePasswordRoutine({ ...passwordValues, clearValues }))
  }

  const handleAccountDelete = useCallback(() => {
    dispatch(deleteAccountRoutine({ password: userPassword }))
  }, [userPassword])

  const hasEmptyValue = useMemo(() => {
    return values(passwordValues).some(e => isEmpty(e))
  }, [passwordValues])

  const handlePasswordChange = (_, value) => setUserPassword(value)

  const handleAccreditationChange = index => (name, value) => {
    const accreditation = accreditationsValues[index]
    const newValue = {
      ...accreditation,
      [name]: value
    }
    setAccreditationsValues(prev => update(index, newValue, prev))
  }

  const handleAccreditationRemove = index => () => {
    if (accreditationsValues.length > 1) {
      setAccreditationsValues(prev => remove(index, 1, prev))
    } else {
      setAccreditationsValues([{
          id: '',
          number: ''
        }]
      )
    }
  }

  const handleAccreditationAdd = () => {
    setAccreditationsValues(prev => ([
      ...prev,
      {
        id: '',
        number: ''
      }
    ]))
  }

  useEffect(() => {
    const length = accreditationsValues.length
    switch (true) {
      case length === 1:
        setAccreditationsValid(true)
        break
      case length > 1:
        setAccreditationsValid(
          accreditationsValues.every(item =>
            !isEmpty(item.id)
          )
        )
        break
      default:
        return () => {}
    }
  }, [accreditationsValues])

  const handleAccreditationsUpdate = e => {
    e.preventDefault()
    dispatch(updateUserRoutine({
      accreditations: isNotNilOrEmpty(head(accreditationsValues).id)
        ? accreditationsValues
        : []
    }))
  }

  const handleGenerateToken = () => {
    dispatch(getPublicApiTokenRoutine())
  }

  const handleDisableTwoFactorModalOpen = () => {
    setDisableTwoFactorModalOpen(true)
  }

  const handleDisableTwoFactorModalClose = () => {
    setDisableTwoFactorModalOpen(false)
  }

  const handleDisableTwoFactor = () => {
    dispatch(disableTwoFactorRoutine({
      currentPassword: twoFactorPassword,
      code: twoFactorCode,
      onSuccess: () => {
        setDisableTwoFactorModalOpen(false)
        setTwoFactorPassword('')
        setTwoFactorCode('')
        setNewRecoveryCodes([])
      }
    }))
  }

  const handleRegenerateRecoveryCodes = () => {
    dispatch(regenerateTwoFactorRecoveryCodesRoutine({
      currentPassword: twoFactorPassword,
      code: twoFactorCode,
      onSuccess: codes => {
        setNewRecoveryCodes(codes || [])
        setTwoFactorPassword('')
        setTwoFactorCode('')
      }
    }))
  }

  const handleDownloadNewRecoveryCodes = () => {
    if (!newRecoveryCodes.length) return

    const content = [
      'Building Passport recovery codes',
      `Generated: ${new Date().toISOString()}`,
      '',
      ...newRecoveryCodes
    ].join('\n')

    const blob = new Blob([content], { type: 'text/plain;charset=utf-8' })
    const url = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = 'building-passport-recovery-codes.txt'
    document.body.appendChild(link)
    link.click()
    link.remove()
    URL.revokeObjectURL(url)
  }

  return (
    <ProfileContainer>
      <ContentWrapper>
        <HeaderWrapper>
          <PageHeader>My account</PageHeader>
          <MobileMenuButton onClick={() => setMobileDrawerOpen(true)}>
            <AccountCircleIcon />
          </MobileMenuButton>
        </HeaderWrapper>
        <Panels>
          <ExpandablePanel
            open
            headerRight={<WhatsAppPrivacyExplainer />}
            title={
              <WhatsAppTitle>
                <WhatsAppIcon style={{ color: '#25d366', fontSize: 22 }} />
                <span>Chat with Guru on WhatsApp</span>
                <NewBadge>NEW</NewBadge>
              </WhatsAppTitle>
            }
          >
            <WhatsAppPanel />
          </ExpandablePanel>
          <ExpandablePanel open title='Basic user data'>
            <UserBox />
          </ExpandablePanel>
          <ExpandablePanel open title='Your organisation'>
            <OrganisationPanel />
          </ExpandablePanel>
          {!isFrs && <ExpandablePanel title='Accreditations / Qualifications'>
            <>
              <Text>
                Please add details of any professional accreditations or
                qualifications you have, along with corresponding membership
                numbers if appropriate. The quality and accuracy of the
                information you add to a Building Passport should be
                appropriate to the professional qualifications you hold.
              </Text>
              {
                accreditationsValues && (
                  <>
                    <InputDescriptionWrapper>
                      <InputDescription>
                        If you don&apos;t see your accreditation type on the list,
                        please <a href='mailto:info@buildingpassport.com'>contact the administrator.</a>
                      </InputDescription>
                    </InputDescriptionWrapper>
                    <AccreditationsFormsWrapper>
                      {
                        accreditationsValues.map((item, index) => {
                          return <AccreditationForm
                            index={index}
                            key={`accreditation-form-${index}`}
                            values={accreditationsValues[index]}
                            onChange={handleAccreditationChange}
                            options={accreditationOptions}
                            onRemove={handleAccreditationRemove}
                            withDelete={!isEmpty(head(accreditationsValues).id) || accreditationsValues.length > 1}
                          />
                        })
                      }
                    </AccreditationsFormsWrapper>
                    <AddAnotherTypeButton onClick={handleAccreditationAdd}>
                      + Add another accreditation
                    </AddAnotherTypeButton>
                    <NextButton
                      disabled={!accreditationsValid}
                      onClick={handleAccreditationsUpdate}
                    >
                      Save
                    </NextButton>
                  </>
                )
              }
            </>
          </ExpandablePanel>
          }
          <ExpandablePanel title='Certifications & training'>
            <CertificationsPanel />
          </ExpandablePanel>
          <ExpandablePanel title='Guru assistant'>
            <GuruSettingsPanel />
          </ExpandablePanel>
          <ExpandablePanel title='Location sharing'>
            <LocationSharingPanel />
          </ExpandablePanel>
          <ExpandablePanel title='Change password'>
            <ChangePasswordDesc>
              Create a new password for your account using the form below.
            </ChangePasswordDesc>
            <Input
              label='Current password'
              value={passwordValues.currentPassword}
              name='currentPassword'
              onChange={handlePasswordValueChange}
              type='password'
            />
            <Input
              label='New password'
              value={passwordValues.newPassword}
              name='newPassword'
              onChange={handlePasswordValueChange}
              type='password'
              withPasswordHint
            />
            <Input
              label='Confirm new password'
              value={passwordValues.newPasswordConfirmation}
              name='newPasswordConfirmation'
              onChange={handlePasswordValueChange}
              type='password'
            />
            <NextButton
              onClick={handleSubmit}
              disabled={hasEmptyValue}
            >
              Change password
            </NextButton>
          </ExpandablePanel>
          <ExpandablePanel title='Two-factor authentication'>
            <Text>
              Add an authenticator app code to your login, or disable it if you no longer want to use two-factor authentication.
            </Text>
            <SecurityStatus enabled={isTwoFactorEnabled}>
              {isTwoFactorEnabled
                ? 'Two-factor authentication is currently enabled.'
                : 'Two-factor authentication is currently disabled.'}
            </SecurityStatus>
            <ApiButtonWrapper>
              {isTwoFactorEnabled ? (
                <>
                  <Input
                    label='Confirm with password'
                    value={twoFactorPassword}
                    name='twoFactorPassword'
                    onChange={(_, value) => setTwoFactorPassword(value)}
                    type='password'
                  />
                  <OrDivider>or</OrDivider>
                  <Input
                    label='Confirm with authenticator code'
                    value={twoFactorCode}
                    name='twoFactorCode'
                    onChange={(_, value) => setTwoFactorCode(value)}
                  />
                  <ButtonGrid>
                    <NextButton
                      onClick={handleRegenerateRecoveryCodes}
                      disabled={!twoFactorPassword && !twoFactorCode}
                    >
                      Regenerate recovery codes
                    </NextButton>
                    <DangerButton
                      type='button'
                      onClick={handleDisableTwoFactorModalOpen}
                      disabled={!twoFactorPassword && !twoFactorCode}
                    >
                      Disable two-factor authentication
                    </DangerButton>
                  </ButtonGrid>
                  {newRecoveryCodes.length > 0 && (
                    <NewRecoveryCodes>
                      <RecoveryHeader>
                        <strong>New recovery codes</strong>
                        <SmallButton type='button' onClick={handleDownloadNewRecoveryCodes}>
                          Download .txt
                        </SmallButton>
                      </RecoveryHeader>
                      <RecoveryNote>
                        Save these now. Old recovery codes no longer work.
                      </RecoveryNote>
                      {newRecoveryCodes.map(item => (
                        <code key={item}>{item}</code>
                      ))}
                    </NewRecoveryCodes>
                  )}
                </>
              ) : (
                <NextButton onClick={() => redirect(PRIVATE_PATHS.twoFactorSetup)}>
                  Set up two-factor authentication
                </NextButton>
              )}
            </ApiButtonWrapper>
          </ExpandablePanel>
          {
            propOr(false, 'canIntegrateWithPublicApi', currentUser) && (
              <ExpandablePanel title='Public API key'>
                <Text>
                  You may be able to integrate your systems with Building Passport using our API. For documentation and
                  to discuss further please email us at <a href='mailto:app@buildingpassport.com'>app@buildingpassport.com</a>.
                  If you or your company is prepared for integration you can generate your API token using this button.
                </Text>
                {
                  isNotNilOrEmpty(apiToken) && (
                    <>
                      <TokenBox breakWord>
                        {apiToken}
                      </TokenBox>
                      <InputDescription margin='20px 0 0'>
                        {`Make note of your token as you won't be able to see it later.`}
                      </InputDescription>
                    </>
                  )
                }
                {
                  propOr(false, 'apiTokenExists', currentUser) && !isNotNilOrEmpty(apiToken) && (
                    <TokenBox>
                      Your API token has already been generated. You cannot see it, but you can replace it by generating a new token.
                    </TokenBox>
                  )
                }
                <ApiButtonWrapper>
                  <NextButton
                    onClick={handleGenerateToken}
                    disabled={isNotNilOrEmpty(apiToken)}
                  >
                    Generate API token
                  </NextButton>
                </ApiButtonWrapper>
              </ExpandablePanel>
            )
          }
          <ExpandablePanel title='Delete account'>
            <Text>
              When you confirm this operation, your account will be closed and you will no longer have access to the
              Building Passport system. In order to keep an accurate history and changelog of a Building Passport your name will
              still be visible against additions you have made to a Building Passport but your sensitive personal
              information (email, contact details, login details, profile history, IP addresses) will be anonymised.
            </Text>
            <Warning>
              WARNING! This operation cannot be undone.
            </Warning>
            <Input
              label='To confirm, enter your password'
              onChange={handlePasswordChange}
              value={userPassword}
              type='password'
            />
            <NextButton
              onClick={handleAccountDelete}
              disabled={isEmpty(userPassword)}
            >
              Delete account
            </NextButton>
          </ExpandablePanel>
        </Panels>
      </ContentWrapper>
      <DetailsWrapper>
        <ProfileInfo />
      </DetailsWrapper>

      <MobileDrawerOverlay
        isOpen={mobileDrawerOpen}
        onClick={() => setMobileDrawerOpen(false)}
      />
      <MobileDrawer isOpen={mobileDrawerOpen}>
        <MobileDrawerHeader>
          <MobileDrawerTitle>Your Profile</MobileDrawerTitle>
          <MobileDrawerClose onClick={() => setMobileDrawerOpen(false)}>
            <CloseIcon />
          </MobileDrawerClose>
        </MobileDrawerHeader>
        <MobileDrawerContent>
          <ProfileInfo />
        </MobileDrawerContent>
      </MobileDrawer>
      <Modal
        open={disableTwoFactorModalOpen}
        title='Disable two-factor authentication'
        onClose={handleDisableTwoFactorModalClose}
        onCancel={handleDisableTwoFactorModalClose}
        onSubmit={handleDisableTwoFactor}
        submitButtonText='Disable'
        withSubmit
        withCancel
      >
        Are you sure you want to disable two-factor authentication for your account?
      </Modal>
    </ProfileContainer>
  )
}

export default ProfileSettings

const ProfileContainer = styled.div`
  display: flex;
`

const HeaderWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
`

const MobileMenuButton = styled.button`
  display: none;
  align-items: center;
  justify-content: center;
  background: transparent;
  border: none;
  padding: 8px;
  cursor: pointer;
  border-radius: 8px;
  transition: background-color 0.2s ease;
  color: ${({ theme }) => theme.colors.haiti || '#333'};
  margin-left: auto;

  @media (max-width: 768px) {
    display: flex;
  }

  &:hover {
    background-color: rgba(0, 0, 0, 0.05);
  }

  svg {
    font-size: 28px;
  }
`

const DetailsWrapper = styled.div`
  width: 280px;
  position: fixed;
  top: ${({ theme }) => theme.dimensions.topBarHeight};
  right: 0;
  bottom: 0;
  background-color: ${({ theme }) => theme.colors.lighterGrey};
  padding: 50px 20px;
  overflow-y: auto;

  @media (max-width: 768px) {
    display: none;
  }
`

const ContentWrapper = styled.div`
  width: 100%;
  padding-right: 300px;

  @media (max-width: 768px) {
    padding-right: 0;
  }
`

const Panels = styled.div`
  max-width: 645px;
  margin: 20px auto 0;

  @media (max-width: 768px) {
    margin: 16px 12px 0;
    max-width: 100%;
  }
`

const WhatsAppTitle = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 10px;
`

const NewBadge = styled.span`
  display: inline-block;
  background: #25d366;
  color: #fff;
  font-size: 10px;
  font-weight: 800;
  letter-spacing: 0.6px;
  padding: 2px 8px;
  border-radius: 10px;
  text-transform: uppercase;
`

const ChangePasswordDesc = styled.div`
  font-size: 15px;
  margin-bottom: 20px;
  color: ${({ theme }) => theme.colors.haiti};
  opacity: .7;
`

const Text = styled.div`
  font-size: 14px;
`

const Warning = styled.div`
  font-size: 14px;
  color: ${({ theme }) => theme.colors.error};
  margin: 20px 0;
  font-weight: bold;
`

const AccreditationsFormsWrapper = styled.div`
  margin-top: 10px;
`

const AddAnotherTypeButton = styled.div`
  text-transform: uppercase;
  color: ${({ theme }) => theme.colors.secondary};
  font-size: 12px;
  font-weight: bold;
  width: fit-content;
  cursor: pointer;
  margin: 10px 0 20px auto;

  ${({ disabled, theme }) => disabled && css`
    color: ${theme.colors.grey[600]};
    cursor: default;
  `}
`

const InputDescriptionWrapper = styled.div`
  margin-top: 15px;
`

const TokenBox = styled.div`
  padding: 10px;
  border: 1px solid ${({ theme }) => theme.colors.grey[500]};
  background-color: ${({ theme }) => theme.colors.grey[400]};
  border-radius: 4px;
  margin-top: 20px;

  ${({ breakWord }) => breakWord && css`
    word-break: break-all;
  `}
`

const ApiButtonWrapper = styled.div`
  margin-top: 20px;
`

const SecurityStatus = styled.div`
  margin-top: 18px;
  padding: 12px 14px;
  border-radius: 6px;
  border: 1px solid ${({ enabled }) => (enabled ? 'rgba(54, 190, 205, 0.35)' : '#e7ebf5')};
  background: ${({ enabled }) => (enabled ? 'rgba(54, 190, 205, 0.12)' : '#f7f8fd')};
  color: ${({ theme }) => theme.colors.haiti};
  font-size: 14px;
`

const DangerButton = styled.button`
  width: 100%;
  min-height: 48px;
  border: 0;
  border-radius: 4px;
  background: ${({ theme }) => theme.colors.error || '#cd2000'};
  color: #ffffff;
  cursor: pointer;
  font-weight: 700;
  padding: 0 18px;

  &:disabled {
    cursor: not-allowed;
    opacity: 0.6;
  }

  &:hover {
    opacity: 0.9;
  }
`

const OrDivider = styled.div`
  color: ${({ theme }) => theme.colors.grey[700]};
  font-size: 12px;
  margin: -4px 0 12px;
  text-align: center;
  text-transform: uppercase;
`

const ButtonGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 12px;
`

const NewRecoveryCodes = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 8px;
  margin-top: 20px;

  code {
    background: #f7f8fd;
    border: 1px solid #e7ebf5;
    border-radius: 6px;
    padding: 8px;
    font-size: 12px;
  }
`

const RecoveryHeader = styled.div`
  grid-column: 1 / -1;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
`

const RecoveryNote = styled.div`
  grid-column: 1 / -1;
  color: ${({ theme }) => theme.colors.grey[700]};
  font-size: 12px;
`

const SmallButton = styled.button`
  background: #ffffff;
  border: 1px solid #cdd4ea;
  border-radius: 6px;
  color: #110c2f;
  cursor: pointer;
  font-size: 12px;
  font-weight: 700;
  height: 34px;
  padding: 0 12px;
`

// Mobile Drawer Components
const MobileDrawerOverlay = styled.div`
  display: none;

  @media (max-width: 768px) {
    display: ${({ isOpen }) => (isOpen ? 'block' : 'none')};
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.5);
    z-index: 1298;
    backdrop-filter: blur(2px);
    transition: opacity 0.3s ease;
    opacity: ${({ isOpen }) => (isOpen ? 1 : 0)};
  }
`

const MobileDrawer = styled.div`
  display: none;

  @media (max-width: 768px) {
    display: flex;
    flex-direction: column;
    position: fixed;
    top: 0;
    right: 0;
    width: 280px;
    max-width: 85vw;
    height: 100vh;
    background: ${({ theme }) => theme.colors.lighterGrey || '#f8f9fa'};
    box-shadow: -2px 0 8px rgba(0, 0, 0, 0.15);
    z-index: 1299;
    transform: translateX(${({ isOpen }) => (isOpen ? '0' : '100%')});
    transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    overflow: hidden;
  }
`

const MobileDrawerHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 20px;
  border-bottom: 1px solid #e0e0e0;
  background: #ffffff;
  min-height: 56px;
`

const MobileDrawerTitle = styled.h3`
  margin: 0;
  font-size: 16px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.haiti || '#333'};
  text-transform: uppercase;
  letter-spacing: 0.5px;
`

const MobileDrawerClose = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  background: transparent;
  border: none;
  padding: 8px;
  cursor: pointer;
  border-radius: 8px;
  color: ${({ theme }) => theme.colors.haiti || '#333'};
  transition: background-color 0.2s ease;

  &:hover {
    background-color: rgba(0, 0, 0, 0.05);
  }

  svg {
    font-size: 24px;
  }
`

const MobileDrawerContent = styled.div`
  flex: 1;
  overflow-y: auto;
  overflow-x: hidden;
  padding: 50px 20px;
  -webkit-overflow-scrolling: touch;
`
